import { AppData } from '@lightningjs/sdk';
import jwtDecode from 'jwt-decode';
import { constants } from 'aliases';
import CwApiClient, { Endpoints } from 'services/CwApiClient';
import { secondsToMilliseconds } from 'support/timeUtils';
import DRMService, {
  DRMData,
  DRMPlatform,
  DrmType,
} from 'services/drm/DRMService';
import { DrmMediaItem, Video } from 'types/api/media';
import { isVideo } from 'support/contentUtils';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';

const client = new CwApiClient();

type DecodedJWTToken = {
  accid: string;
  aud: string;
  exp: number;
  iat: number;
  pro: string;
};

export default class BrightcoveDrmService extends DRMService {
  readonly platform = DRMPlatform.BRIGHTCOVE;

  private jwtToken: string | undefined;

  constructor() {
    super();
  }

  private getDrmType(): DrmType {
    return AppData?.device.getDrmType() ?? DrmType.WIDEVINE;
  }

  private shouldUpdateJwtToken() {
    const jwtToken = this.jwtToken;
    if (!jwtToken) return true;

    try {
      const currentDate = new Date();
      const parsedJwtToken = jwtDecode(jwtToken) as DecodedJWTToken;
      // JWT expiry date is in seconds since epoch
      const tokenExpiryMs = secondsToMilliseconds(parsedJwtToken.exp);
      const expiryDate = new Date(tokenExpiryMs);

      return currentDate >= expiryDate;
    } catch (e) {
      reportError({ error: e, userFacing: false, code: ErrorCode.c24 });
      return true;
    }
  }

  private async fetchJwtToken(): Promise<string | undefined> {
    try {
      const endpoint = Endpoints.JWT_API;
      const url = endpoint.replace('{{DRM_TYPE}}', this.getDrmType());

      const response = await client.request<Record<string, string>>('GET', url);
      return response.data?.value;
    } catch (e) {
      reportError({ error: e, userFacing: true, code: ErrorCode.c25 });
      return undefined;
    }
  }

  private async lazyUpdateJwtToken() {
    const shouldUpdateJwtToken = this.shouldUpdateJwtToken();
    if (!shouldUpdateJwtToken) return;

    this.jwtToken = await this.fetchJwtToken();
  }

  private getDRMLicensePath(contentId: string) {
    const { basePath } = constants.brightcove.playback;
    const accountId = AppData?.brightcove.accountId;
    const drmType = this.getDrmType();
    return `${basePath}/accounts/${accountId}/videos/${contentId}/licenses/cenc/${drmType}`;
  }

  private async getDRMLicenseParams() {
    await this.lazyUpdateJwtToken();

    const jwtToken = this.jwtToken;
    return jwtToken ? `bcov_auth=${encodeURIComponent(jwtToken)}` : undefined;
  }

  private async getDRMLicenseURL(video: Video) {
    const id = video.bcVideoId;
    if (!id) return undefined;

    const basePath = this.getDRMLicensePath(id);
    const params = await this.getDRMLicenseParams();
    if (!params) return undefined;

    return `${basePath}?${params}`;
  }

  async getDRMData(
    content: Video | DrmMediaItem,
  ): Promise<DRMData | undefined> {
    // Brightcove does not support DrmMediaItems
    if (!isVideo(content)) return undefined;

    const licenseUrl = await this.getDRMLicenseURL(content as Video);
    return {
      hasDrm: true, // All Brightcove videos will be DRM enabled
      licenseUrl,
      videoSrc: undefined, // DRM live content is not available on Brightcove
      token: this.jwtToken,
    };
  }
}
